import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ChevronLeft,
  ChevronRight,
  Timer,
  AlertTriangle,
  CheckCircle,
  Download,
  FileEdit,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatTime, formatMinutes, formatDecimalHours } from './utils';

const getStatusBadge = (entryStatus) => {
  switch (entryStatus) {
    case 'clocked_in':
      return <Badge className="bg-green-500">Arbeitet</Badge>;
    case 'on_break':
      return <Badge className="bg-amber-500">Pause</Badge>;
    case 'clocked_out':
      return <Badge variant="secondary">Beendet</Badge>;
    default:
      return <Badge variant="outline">-</Badge>;
  }
};

const downloadArbZGReport = async (selectedMonth) => {
  try {
    toast.info('ArbZG-Report wird erstellt...');
    const y = selectedMonth.getFullYear();
    const m = selectedMonth.getMonth() + 1;
    const response = await api.get(`/time-tracking/compliance-report?year=${y}&month=${m}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ArbZG_Report_${y}_${m}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('ArbZG-Report heruntergeladen');
  } catch {
    toast.error('Fehler beim Erstellen des Reports');
  }
};

export const MonthView = ({ selectedMonth, onChangeMonth, monthlyData, monthlyLoading, onOpenCorrection }) => {
  return (
    <>
      <div className="flex items-center justify-center gap-2 mb-4">
        <Button variant="outline" size="icon" onClick={() => onChangeMonth(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium px-2 min-w-[150px] text-center">
          {format(selectedMonth, 'MMMM yyyy', { locale: de })}
        </span>
        <Button variant="outline" size="icon" onClick={() => onChangeMonth(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {monthlyLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : monthlyData ? (
        <>
          {/* Soll/Ist Überstunden Card */}
          {monthlyData.overtime && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl border bg-card" data-testid="overtime-card">
              <div className="flex items-center gap-2 mb-3">
                <Timer className="h-5 w-5 text-primary" />
                <h3 className="text-sm sm:text-base font-semibold">Soll / Ist Vergleich</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-secondary/30 rounded-lg text-center">
                  <p className="text-[11px] sm:text-xs text-muted-foreground">Soll</p>
                  <p className="text-base sm:text-lg font-bold font-mono break-words">{formatDecimalHours(monthlyData.overtime.target_hours)} <span className="text-[10px] sm:text-base">Std</span></p>
                </div>
                <div className="p-2 sm:p-3 bg-secondary/30 rounded-lg text-center">
                  <p className="text-[11px] sm:text-xs text-muted-foreground">Ist</p>
                  <p className="text-base sm:text-lg font-bold font-mono break-words">{formatDecimalHours(monthlyData.overtime.actual_hours)} <span className="text-[10px] sm:text-base">Std</span></p>
                </div>
                <div className={`p-2 sm:p-3 rounded-lg text-center ${monthlyData.overtime.overtime_hours >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  <p className="text-[11px] sm:text-xs text-muted-foreground">Differenz</p>
                  <p className={`text-base sm:text-lg font-bold font-mono break-words ${monthlyData.overtime.overtime_hours >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {monthlyData.overtime.overtime_hours > 0 ? '+' : ''}{formatDecimalHours(monthlyData.overtime.overtime_hours)} <span className="text-[10px] sm:text-base">Std</span>
                  </p>
                </div>
                <div className={`p-2 sm:p-3 rounded-lg text-center ${monthlyData.overtime.avg_6m_compliant ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  <p className="text-[11px] sm:text-xs text-muted-foreground">6M-Schnitt</p>
                  <p className={`text-base sm:text-lg font-bold font-mono break-words ${monthlyData.overtime.avg_6m_compliant ? 'text-green-600' : 'text-red-600'}`}>
                    {formatDecimalHours(monthlyData.overtime.avg_daily_hours_6m)} <span className="text-[10px] sm:text-base">Std</span>
                  </p>
                </div>
              </div>
              {monthlyData.overtime.avg_6m_warning && (
                <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                  <p className="text-xs text-red-700 dark:text-red-300">{monthlyData.overtime.avg_6m_warning}</p>
                </div>
              )}
            </div>
          )}

          {/* Stundenkonto: kumulierter Gesamtsaldo */}
          {monthlyData.cumulative_overtime && (
            <div
              className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl border bg-card"
              data-testid="cumulative-overtime-card"
            >
              <div className="flex items-center gap-2 mb-3">
                <Timer className="h-5 w-5 text-primary" />
                <h3 className="text-sm sm:text-base font-semibold">Stundenkonto (Gesamtsaldo)</h3>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-secondary/30 rounded-lg text-center">
                  <p className="text-[11px] sm:text-xs text-muted-foreground">Startsaldo</p>
                  <p className="text-base sm:text-lg font-bold font-mono break-words">
                    {monthlyData.cumulative_overtime.starting_balance > 0 ? '+' : ''}
                    {formatDecimalHours(monthlyData.cumulative_overtime.starting_balance)}{' '}
                    <span className="text-[10px] sm:text-base">Std</span>
                  </p>
                </div>
                <div
                  className={`p-2 sm:p-3 rounded-lg text-center ${monthlyData.cumulative_overtime.cumulative_balance >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}
                >
                  <p className="text-[11px] sm:text-xs text-muted-foreground">Gesamtsaldo (bis Monatsende)</p>
                  <p
                    className={`text-base sm:text-lg font-bold font-mono break-words ${monthlyData.cumulative_overtime.cumulative_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    data-testid="cumulative-overtime-balance"
                  >
                    {monthlyData.cumulative_overtime.cumulative_balance > 0 ? '+' : ''}
                    {formatDecimalHours(monthlyData.cumulative_overtime.cumulative_balance)}{' '}
                    <span className="text-[10px] sm:text-base">Std</span>
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Fortlaufend addierte Über-/Minusstunden inkl. Startsaldo
                {monthlyData.cumulative_overtime.first_month
                  ? ` (kumuliert ab ${monthlyData.cumulative_overtime.first_month})`
                  : ''}
                .
              </p>
            </div>
          )}

          {/* Monthly Summary Cards */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="p-2 sm:p-4 bg-secondary/30 rounded-lg text-center">
              <p className="text-[11px] sm:text-sm text-muted-foreground leading-tight">Tage</p>
              <p className="text-base sm:text-2xl font-bold font-mono">{monthlyData.days_worked}</p>
            </div>
            <div className="p-2 sm:p-4 bg-secondary/30 rounded-lg text-center">
              <p className="text-[11px] sm:text-sm text-muted-foreground leading-tight">Pausen</p>
              <p className="text-base sm:text-2xl font-bold font-mono break-words">{formatDecimalHours(monthlyData.total_break_hours)} <span className="text-[10px] sm:text-base">Std</span></p>
            </div>
            <div className="p-2 sm:p-4 bg-primary/10 rounded-lg text-center">
              <p className="text-[11px] sm:text-sm text-muted-foreground leading-tight">Netto</p>
              <p className="text-base sm:text-2xl font-bold font-mono text-primary break-words">{formatDecimalHours(monthlyData.total_work_hours)} <span className="text-[10px] sm:text-base">Std</span></p>
            </div>
          </div>

          {/* Abwesenheiten in diesem Monat */}
          {monthlyData.overtime?.absence_days_total > 0 && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl border bg-card" data-testid="absences-summary">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-rose-500" />
                <h3 className="text-sm sm:text-base font-semibold">
                  Abwesenheiten im Monat ({monthlyData.overtime.absence_days_total} {monthlyData.overtime.absence_days_total === 1 ? 'Tag' : 'Tage'})
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
                {[
                  { key: 'U', label: 'Urlaub', cls: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' },
                  { key: 'K', label: 'Krankheit', cls: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' },
                  { key: 'KK', label: 'Kind krank', cls: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300' },
                  { key: 'EZ', label: 'Elternzeit', cls: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' },
                  { key: 'KO', label: 'KO-Tag', cls: 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' },
                ].map(({ key, label, cls }) => {
                  const days = monthlyData.overtime.absence_days?.[key] || 0;
                  return (
                    <div key={key} className={`p-2 sm:p-3 rounded-lg text-center ${cls}`}>
                      <p className="text-[11px] sm:text-xs opacity-80">{label}</p>
                      <p className="text-base sm:text-lg font-bold font-mono">{days} <span className="text-[10px] sm:text-sm">Tg</span></p>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Diese Tage werden vom Soll abgezogen (in den Firmeneinstellungen konfigurierbar).
              </p>
            </div>
          )}

          {/* Retention Info + ArbZG Report */}
          {monthlyData.retention && (
            <div className="mb-4 flex items-center justify-between gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg" data-testid="retention-info">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  {monthlyData.retention.info} ({monthlyData.retention.law_reference})
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="flex-shrink-0 text-xs h-7 gap-1 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-800"
                data-testid="arbzg-report-btn"
                onClick={() => downloadArbZGReport(selectedMonth)}
              >
                <Download className="h-3 w-3" />
                ArbZG-Report
              </Button>
            </div>
          )}

          {/* Monthly Entries Table */}
          <div className="rounded-lg border max-h-[400px] overflow-auto">
            <Table className="min-w-[560px]">
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Kommen</TableHead>
                  <TableHead>Gehen</TableHead>
                  <TableHead>Pausen</TableHead>
                  <TableHead>Netto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyData.entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Keine Einträge in diesem Monat
                    </TableCell>
                  </TableRow>
                ) : (
                  monthlyData.entries.map((entry) => (
                    <TableRow key={entry.id || entry.date}>
                      <TableCell className="font-medium">
                        {format(parseISO(entry.date), 'EEE, dd.MM.', { locale: de })}
                      </TableCell>
                      <TableCell>{formatTime(entry.clock_in)}</TableCell>
                      <TableCell>{formatTime(entry.clock_out)}</TableCell>
                      <TableCell>{entry.break_minutes || 0} min</TableCell>
                      <TableCell className="font-medium">{formatMinutes(entry.work_minutes || 0)}</TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell>
                        {onOpenCorrection && entry.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onOpenCorrection(entry)}
                            title="Korrektur beantragen"
                            data-testid={`month-correction-btn-${entry.date}`}
                          >
                            <FileEdit className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      ) : (
        <div className="text-center text-muted-foreground py-8">
          Fehler beim Laden der Monatsdaten
        </div>
      )}
    </>
  );
};
